import api from "api";
import { supportApiBase } from "api/constants";
import { useQuery } from "react-query";
import { queryKeys } from "utils/misc";
import { ALL_LOCALES } from "../views/lessons/constants";
import {
  LessonFormData,
  LocalizedAnswer,
  LocalizedContentBlock,
  LocalizedQuestion,
  PartFormData,
  emptyLocalizedText,
} from "../views/lessons/types";
import { writeLocale } from "../views/lessons/utils";
import { LessonDto, PartDto } from "./lessonDtos";

/** The api client throws Error(JSON.stringify({ status, ... })). */
const isNotFound = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  try {
    return JSON.parse(err.message).status === 404;
  } catch {
    return false;
  }
};

// Merge N locale variants of the same lesson into a single LessonFormData.
export const mergeLessonLocales = (byLocale: Partial<Record<string, LessonDto>>): LessonFormData => {
  // Lesson-level fields (id, state, target_joints) are identical across locales,
  // so any returned response will do.
  const any = Object.values(byLocale).find(Boolean) as LessonDto | undefined;
  if (!any) return { state: "draft", target_joints: [], parts: [] };

  const partsById = new Map<number, PartFormData>();
  const orderByPartId = new Map<number, number>();

  for (const locale of Object.keys(byLocale)) {
    const lesson = byLocale[locale];
    if (!lesson) continue;
    for (const apiPart of lesson.parts ?? []) {
      const key = apiPart.id ?? -1;
      let formPart = partsById.get(key);
      if (!formPart) {
        formPart = initPartFromApi(apiPart);
        partsById.set(key, formPart);
        orderByPartId.set(key, apiPart.order);
      }
      if (apiPart.translation) applyTranslation(formPart, locale, apiPart);
    }
  }

  const parts = Array.from(partsById.entries())
    .sort((a, b) => (orderByPartId.get(a[0]) ?? 0) - (orderByPartId.get(b[0]) ?? 0))
    .map(([, p]) => p);

  return { id: any.id, state: any.state, target_joints: any.target_joints, creator_id: any.creator_id, parts };
};

const initPartFromApi = (apiPart: PartDto): PartFormData => ({
  id: apiPart.id,
  translationIds: {},
  title: emptyLocalizedText(),
  imageUrl: emptyLocalizedText(),
  content: [],
  questions: (apiPart.questions ?? []).map(q => ({
    id: q.id,
    title: emptyLocalizedText(),
    tipDescription: emptyLocalizedText(),
    answers: buildEmptyAnswers(q.correct_alternative),
  })),
});

// Scaffold placeholder answers before translation data is applied. The real
// answer count and correct flags come from translation.answers[].correct
// (which supports multiple correct answers); this just ensures the array is
// long enough for applyTranslation to merge into by index.
const buildEmptyAnswers = (correctAlt: number): LocalizedAnswer[] => {
  const count = Math.max(2, correctAlt + 1);
  return Array.from({ length: count }, (_, i) => ({
    correct: i === correctAlt,
    text: emptyLocalizedText(),
  }));
};

const applyTranslation = (part: PartFormData, locale: string, apiPart: PartDto) => {
  const tr = apiPart.translation;
  if (!tr) return;
  if (tr.id != null) {
    part.translationIds = { ...part.translationIds, [locale]: tr.id };
  }
  part.title = writeLocale(part.title, locale, tr.title ?? "");
  // Only carry the image URL into form state when the backend actually has
  // one. Empty strings / nullish values would otherwise trigger PollingImage
  // to spin indefinitely trying to load a non-URL. The backend also has a
  // bug where, when no image is set, it leaks the template variable name
  // and returns something like "https://<cdn>image_url" — treat those as
  // empty too.
  if (tr.image_url && !tr.image_url.endsWith("image_url")) {
    part.imageUrl = writeLocale(part.imageUrl, locale, tr.image_url);
  }

  // Content: union by index (all locales share same order of blocks).
  const blocks: LocalizedContentBlock[] = tr.content.map((b, i) => {
    const value = b.content ?? "";
    const existing = part.content[i];
    return existing
      ? { ...existing, content: writeLocale(existing.content, locale, value) }
      : { type: b.type, content: writeLocale(emptyLocalizedText(), locale, value) };
  });
  for (let i = blocks.length; i < part.content.length; i++) blocks.push(part.content[i]);
  part.content = blocks;

  // Questions: merge by index
  part.questions = part.questions.map((existing, i) => {
    const translatedQ = tr.questions[i];
    if (!translatedQ) return existing;
    const answers: LocalizedAnswer[] = existing.answers.map((a, ai) => {
      const translatedA = translatedQ.answers[ai];
      if (!translatedA) return a;
      return {
        correct: translatedA.correct ?? a.correct,
        text: writeLocale(a.text, locale, translatedA.text ?? ""),
      };
    });
    while (answers.length < translatedQ.answers.length) {
      const idx = answers.length;
      const translatedA = translatedQ.answers[idx];
      answers.push({
        correct: !!translatedA.correct,
        text: writeLocale(emptyLocalizedText(), locale, translatedA.text ?? ""),
      });
    }
    return {
      ...existing,
      title: writeLocale(existing.title, locale, translatedQ.title ?? ""),
      tipDescription: writeLocale(existing.tipDescription, locale, translatedQ.tip_description ?? ""),
      answers,
    };
  }) as LocalizedQuestion[];
};

export const useGetLesson = (id: number | null) =>
  useQuery(
    queryKeys.lesson(id ?? 0),
    async () => {
      if (!id) throw new Error("no id");
      const results = await Promise.all(
        ALL_LOCALES.map(async locale => {
          try {
            const path = `GET /lessons/${id}?locale=${encodeURIComponent(locale)}`;
            // @ts-ignore — v2 endpoint, not in OpenAPI types
            const lesson: LessonDto = await api.parse(path, { root: supportApiBase });
            return [locale, lesson] as const;
          } catch (err) {
            // A 404 means no translation exists for this locale yet — that's
            // expected and safe to skip. Any other error (401, 500, network)
            // should propagate so the UI shows a load failure.
            if (isNotFound(err)) return [locale, null] as const;
            throw err;
          }
        })
      );
      const byLocale: Partial<Record<string, LessonDto>> = {};
      for (const [locale, lesson] of results) {
        if (lesson) byLocale[locale] = lesson;
      }
      if (Object.keys(byLocale).length === 0) {
        throw new Error(`Lesson ${id} not found`);
      }
      return mergeLessonLocales(byLocale);
    },
    { enabled: !!id }
  );
